import { useMemo } from 'react';
import { useSubmittedProgramsOptional } from '../../providers/SubmittedProgramsProvider';
import type { Result } from '../../types/result';

/**
 * Platform-owned label for the submitted-card affordance. The hen-ui
 * `DegreeCardDetailed` has no dedicated `submitted`
 * prop, so the platform overrides `ctaLabel` with this constant once a
 * lead was submitted. It is intentionally NOT authorable — the
 * submitted-state rule stays governed in runtime code; an author's bound
 * `ctaLabel` is used pre-submission only. Shared across every
 * submitted-aware surface (`DslDegreeCardDetailed`, `Spotlight`) so the
 * three sites cannot drift.
 */
export const SUBMITTED_CTA_LABEL = 'Submitted';

/**
 * Shape returned by {@link useSubmittedForProgram}. Kept deliberately
 * narrow — the hen-ui `DegreeCardDetailed` has no dedicated "submitted"
 * prop, so `DslDegreeCardDetailed` reads this boolean and overrides the
 * card's `ctaLabel` / `onCtaClick` itself.
 */
export type SubmittedProps = {
  isProgramSubmitted: boolean;
};

/**
 * Derives the submitted-state for a single program by reading the
 * optional {@link useSubmittedProgramsOptional} context. When no provider
 * is in the tree, or no concrete program was supplied, the hook returns
 * `{ isProgramSubmitted: false }` so a card renders as a non-submitted
 * card without crashing (used by Recommender / Spotlight popups + any
 * caller that hasn't mounted a `SubmittedProgramsProvider`).
 *
 * Mirrors `useComparisonForProgram` — the read-only counterpart for
 * submitted-state exposure.
 */
export function useSubmittedForProgram(program?: Result): SubmittedProps {
  const submitted = useSubmittedProgramsOptional();
  // Depend on the snapshot (reference-stable across no-op renders) so a
  // stable program reference yields a referentially stable result.
  const snapshot = submitted?.snapshot;

  return useMemo<SubmittedProps>(() => {
    if (!submitted || !program) {
      return { isProgramSubmitted: false };
    }
    return {
      isProgramSubmitted: submitted.isProgramSubmitted(program.programId),
    };
    // `submitted` callbacks are stable for a given `snapshot`; depending
    // on `snapshot` (not the wrapper) avoids re-computing on no-op renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [program, snapshot]);
}
